<template>
  <div :class="$forms.stepperContainer">
    <app-loader :show-loader="getLoadingStatus" />

    <app-errors-dialog :value="errorsDialog"
                       @close="closeErrorsDialog">
      <p v-if="otherIncomeDescriptionRequired"
         :class="$forms.errorMessage">
        Please, describe your other income
      </p>

      <p v-if="otherIncomeDescriptionTooLong"
         :class="$forms.errorMessage">
        Other Income Description is too long
      </p>

      <div v-if="serverErrors">
        <div v-for="value in serverErrors"
             :key="value.error">
          <p v-for="(error, index) in value"
             :key="index"
             :class="$forms.errorMessage">
            {{ error }}
          </p>
        </div>
      </div>
    </app-errors-dialog>

    <div :class="$forms.formMainContainer">
      <h2>Non-Wage Household Income</h2>
      <p>
        List Monthly amounts. For Self-Employment, list the monthly amount received after expenses.
      </p>

      <div :class="$forms.formBox">
        <form>
          <div :class="$forms.formRow">

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.alimonyIncome.$model"
                                  :value="household.alimonyIncome"
                                  :tooltip="true"
                                  label="Alimony Income">
                <template v-slot:tooltipHeader>
                  Alimony Income
                </template>
                <template v-slot:tooltipText>
                  If you receive alimony payments from a former spouse, enter
                  the monthly alimony amount you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.alimonyIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Alimony Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.interestIncome.$model"
                                  :value="household.interestIncome"
                                  :tooltip="true"
                                  label="Interest Income">
                <template v-slot:tooltipHeader>
                  Interest Income
                </template>
                <template v-slot:tooltipText>
                  If you receive interest payments from loans,
                  deposits, investments or other sources enter the average
                  monthly interest amount you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.interestIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Interest Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="household.netRentalIncome"
                                  :value="household.netRentalIncome"
                                  label="Net Rental Income"
                                  :disabled="true"/>
            </div>

          </div>

          <div :class="$forms.formRow">

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.childSupportIncome.$model"
                                  :value="household.childSupportIncome"
                                  :tooltip="true"
                                  label="Child Support Income">
                <template v-slot:tooltipHeader>
                  Child Support Income
                </template>
                <template v-slot:tooltipText>
                  If you receive child support payments from a former spouse,
                  enter the monthly child support amount you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.childSupportIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Child Support Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.unemploymentIncome.$model"
                                  :value="household.unemploymentIncome"
                                  :tooltip="true"
                                  label="Unemployment Income">
                <template v-slot:tooltipHeader>
                  Unemployment Income
                </template>
                <template v-slot:tooltipText>
                  If you are currently unemployed and receive unemployment
                  compensation, enter the monthly unemployment compensation you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.unemploymentIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Unemployment Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.socialSecurityIncome.$model"
                                  :value="household.socialSecurityIncome"
                                  label="Social Security Income">
                <template v-slot:tooltipHeader>
                  Social Security Income
                </template>
                <template v-slot:tooltipText>
                  If you receive Social Security income(s) enter the total
                  monthly Social Security amount you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.socialSecurityIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Social Security Income can't be less than 0
              </p>
            </div>

          </div>

          <div :class="$forms.formRow">

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.netSelfEmploymentIncome.$model"
                                  :value="household.netSelfEmploymentIncome"
                                  :tooltip="true"
                                  label="Net Self Employment Income">
                <template v-slot:tooltipHeader>
                  Net Self Employment Income
                </template>
                <template v-slot:tooltipText>
                  <p :class="$style.tooltipText">
                    If you generate self-employment income, enter the monthly amount you
                    receive after deducting monthly business expenses
                  </p>
                  <p :class="$style.tooltipMiddleText">OR</p>
                  <p :class="$style.tooltipText">
                    Go to your most recent tax return, Schedule C, line 31, and divide that
                    number by 12. Then enter that number in this box.
                  </p>
                </template>
              </app-currency-input>

              <p v-if="!$v.household.netSelfEmploymentIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Net Self Employment Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.pensionIncome.$model"
                                  :value="household.pensionIncome"
                                  :tooltip="true"
                                  label="Pension Income">
                <template v-slot:tooltipHeader>
                  Pension Income
                </template>
                <template v-slot:tooltipText>
                  If you receive a pension income enter the average monthly
                  amount of the pension payment you receive
                </template>
              </app-currency-input>

              <p v-if="!$v.household.pensionIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Pension Income can't be less than 0
              </p>
            </div>
          </div>

          <div :class="[$forms.formRow, $style.otherIncomeRow]">
            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-currency-input v-model="$v.household.otherIncome.$model"
                                  :value="household.otherIncome"
                                  label="Other Income"
                                  :tooltip="true">
                <template v-slot:tooltipHeader>Other Income</template>
                <template v-slot:tooltipText>
                  If you are the beneficiary of any other income not stated above,
                  enter the nature and the amount of the payment
                </template>
              </app-currency-input>

              <p v-if="!$v.household.otherIncome.mustBeZeroOrPositive"
                 :class="$forms.errorMessage">
                Other Income can't be less than 0
              </p>
            </div>

            <div :class="[$forms.inputGroup, $style.shortGroup]">
              <app-input-group v-model.trim="$v.household.otherIncomeDescription.$model"
                               label="Describe in one word the nature of your
                                other income (ex.gardening, plumbing, cooking):"
                               class="label-wrap"
                               type="text"/>

              <p v-if="otherIncomeDescriptionRequired"
                 :class="$forms.errorMessage">
                Please, describe your other income
              </p>

              <p v-if="otherIncomeDescriptionTooLong"
                 :class="$forms.errorMessage">
                Other Income Description is too long
              </p>
            </div>
          </div>

          <div v-if="serverErrors">
            <div v-for="value in serverErrors"
                 :key="value.error">
              <p v-for="(error, index) in value"
                 :key="index"
                 :class="$forms.errorMessage">
                {{ error }}
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { maxLength, requiredIf } from 'vuelidate/lib/validators';
import formsMixin from '@/app/core/mixins/forms-mixin';
import { mustBeZeroOrPositive } from '@/utils/validation';
import { USER_ACCESS_TYPES } from '@/constants';

export default {
  data() {
    return {
      step: 6,
      household: {
        alimonyIncome: 0,
        interestIncome: 0,
        netRentalIncome: 0,
        childSupportIncome: 0,
        unemploymentIncome: 0,
        socialSecurityIncome: 0,
        netSelfEmploymentIncome: 0,
        pensionIncome: 0,
        otherIncome: 0,
        otherIncomeDescription: '',
      },
      serverErrors: {},
      initialHousehold: null,
    };
  },
  methods: {
    ...mapActions([
      'postHousehold',
    ]),
    /* Send data to the server and after that emit 'nextStepDone' event
       that will make the Stepper go to the next step */
    sendFormData() {
      const assetsSection = this.getSections.find(section => section.sectionName === 'Assets');
      if (this.lodash.isEqual(this.initialHousehold, this.household) && assetsSection.isCompeted) {
        if (this.getAppAccessType === USER_ACCESS_TYPES.Business) {
          this.$router.push({
            params: { id: this.getCPAId },
            name: 'business',
          });
        } else {
          this.$router.push({
            params: { id: this.getCPAId },
            name: 'expenses',
          });
        }
        return;
      }

      const newItem = {
        alimonyIncome: this.household.alimonyIncome,
        interestIncome: this.household.interestIncome,
        childSupportIncome: this.household.childSupportIncome,
        unemploymentIncome: this.household.unemploymentIncome,
        socialSecurityIncome: this.household.socialSecurityIncome,
        netSelfEmploymentIncome: this.household.netSelfEmploymentIncome,
        pensionIncome: this.household.pensionIncome,
        otherIncome: this.household.otherIncome,
        otherIncomeDescription: this.household.otherIncomeDescription,
      };
      this.postHousehold({
        newItem,
        userId: this.getUserId,
      })
        .then(() => {
          this.serverErrors = {};
          if (this.getAppAccessType === USER_ACCESS_TYPES.Business) {
            this.$router.push({
              params: { id: this.getCPAId },
              name: 'business',
            });
          } else {
            this.$router.push({
              params: { id: this.getCPAId },
              name: 'expenses',
            });
          }
        })
        .catch((err) => {
          this.handleErrors(err);
        });
    },
    /* Set the form fields to the values that were gotten from the server */
    setHousehold() {
      Object.keys(this.getHousehold).forEach((key) => {
        this.household[key] = this.getHousehold[key];
      });

      this.initialHousehold = { ...this.household };
    },
    /* Reset all fields of the form and reset $v value */
    resetForm() {
      Object.keys(this.household).forEach((key) => {
        this.household[key] = 0;
      });
      this.$v.$reset();
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
      'getHousehold',
      'getHouseholdLoadingStatus',
      'getAppAccessType',
      'getSections',
      'getCPAId',
    ]),
    getLoadingStatus() {
      return this.getHouseholdLoadingStatus === 'loading';
    },
    otherIncomeDescriptionRequired() {
      return this.$v.household.otherIncomeDescription.$error
        && !this.$v.household.otherIncomeDescription.required;
    },
    otherIncomeDescriptionTooLong() {
      return this.$v.household.otherIncomeDescription.$error
        && !this.$v.household.otherIncomeDescription.maxLength;
    },
  },
  watch: {
    /* Watch when the server data are downloaded to set form fields */
    getHousehold() {
      this.setHousehold();
    },
  },
  mixins: [
    formsMixin,
  ],
  validations: {
    household: {
      alimonyIncome: {
        mustBeZeroOrPositive,
      },
      interestIncome: {
        mustBeZeroOrPositive,
      },
      childSupportIncome: {
        mustBeZeroOrPositive,
      },
      unemploymentIncome: {
        mustBeZeroOrPositive,
      },
      socialSecurityIncome: {
        mustBeZeroOrPositive,
      },
      netSelfEmploymentIncome: {
        mustBeZeroOrPositive,
      },
      pensionIncome: {
        mustBeZeroOrPositive,
      },
      otherIncome: {
        mustBeZeroOrPositive,
      },
      otherIncomeDescription: {
        required: requiredIf(vm => vm.otherIncome > 0),
        maxLength: maxLength(20),
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.shortGroup {
  width: 268px;

  input {
    /* Overriding Vuetify style */
    width: 268px !important;
  }
}

p.tooltipText {
  color: $white-color;
  font-size: 1.5rem;
  margin-bottom: 6px;
}

p.tooltipMiddleText {
  text-align: center;
  color: $white-color;
  font-weight: 700;
  margin-bottom: 6px;
}

@media screen and (max-width: 1480px) {
  .otherIncomeRow {
    align-items: flex-end;
  }
}
</style>

<style lang="scss">
@media screen and (max-width: 1480px) {
  .label-wrap {
    .v-text-field .v-label {
      white-space: normal;
    }
  }
}
</style>
